import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { Store } from 'src/store/';
import isPopupActive from 'src/store/app/selector/isPopupActive';
import hidePopupOperation from 'src/store/app/operation/hidePopupOperation';
import getIsMobile from 'src/store/app/selector/getIsMobile';
import { BsX } from 'react-icons/bs';
import styles from './index.module.scss';

interface OwnProps {
  popupId: string;
  rounded?: boolean;
  wide?: boolean;
  showCloseInTheHeader?: boolean;
  showCloseOnMobile?: boolean;
  title?: string;
  applyButtonTitle?: string;
  applyButton?: React.ReactNode;
  children: React.ReactNode;
  onClosePopup?: () => void;
  onShowPopup?: () => void;
  onApplyClick?: () => void;
  mobileFloat?: boolean;
  // When false, the popup mounts its children (so they can prefetch) but stays
  // invisible and does not lock scroll. Defaults to true for normal popups.
  visible?: boolean;
}

interface Props extends OwnProps {}

const Popup = ({
  popupId,
  rounded = false,
  wide = false,
  showCloseOnMobile = false,
  showCloseInTheHeader = true,
  mobileFloat = false,
  title,
  applyButtonTitle,
  applyButton,
  children,
  onClosePopup,
  onShowPopup,
  onApplyClick,
  visible = true,
}: Props) => {
  const isActive = useSelector((store: Store) => isPopupActive(store, popupId));
  const isMobile = useSelector(getIsMobile);
  const dispatch = useDispatch();

  if (isActive && visible) {
    document.documentElement.getElementsByTagName('body')[0].style.overflow = 'hidden';
  }

  const applyClick = () => {
    if (onApplyClick) onApplyClick();
    dispatch(hidePopupOperation());
  };

  const closeClick = () => {
    if (onClosePopup) onClosePopup();
    dispatch(hidePopupOperation());
  };

  useEffect(
    () => () => {
      if (onShowPopup) onShowPopup();
    },
    [isActive],
  );

  if (!isActive) return null;

  const showCloseIcon = showCloseInTheHeader && (!isMobile || showCloseOnMobile);

  return (
    <div
      className={cn(styles.popup, {
        [styles.rounded]: rounded,
        [styles.wide]: wide,
        [styles.mobile]: isMobile,
        [styles.mobileFloat]: mobileFloat,
        [styles.hidden]: !visible,
      })}
    >
      {title && (
        <div
          className={cn(styles.header, {
            [styles.desktop]: !isMobile,
          })}
        >
          {title}
        </div>
      )}
      {showCloseIcon && (
        <div className={styles.closeIcon} onClick={closeClick}>
          <BsX size="40px" />
        </div>
      )}
      <div className={styles.popupContent}>{children}</div>
      {applyButton ? (
        <div className={styles.applyFooter}>{applyButton}</div>
      ) : (
        applyButtonTitle && (
          <div className={styles.applyButton} onClick={applyClick}>
            {applyButtonTitle}
          </div>
        )
      )}
    </div>
  );
};

export default Popup;
